import { useParams, Link, Navigate } from "react-router-dom";
import { Calendar, Clock, User, ArrowLeft, ArrowUpRight, Stethoscope, BadgeCheck } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import { CtaBand } from "@/components/common/CtaBand";
import { FaqSection } from "@/components/home/FaqSection";
import { Seo } from "@/components/common/Seo";
import { RichText } from "@/components/common/RichText";
import { MedicalSources } from "@/components/common/MedicalSources";
import { buildBlogPostingSchema, buildBreadcrumbSchema, buildFaqSchema } from "@/lib/schema";
import { getPost, BLOG_POSTS, CLUSTER_LABELS, POST_TREATMENTS } from "@/data/blog";
import { CONTACT, DOCTOR, IMAGES, TREATMENTS } from "@/data/site";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const related = (post.related || [])
    .map((s) => BLOG_POSTS.find((p) => p.slug === s))
    .filter(Boolean);

  const treatmentLinks = (POST_TREATMENTS[post.slug] || [])
    .map((s) => TREATMENTS.find((t) => t.slug === s))
    .filter(Boolean);

  return (
    <div data-testid="blog-post-page">
      <Seo
        title={post.metaTitle}
        description={post.metaDescription}
        path={`/blog/${post.slug}`}
        jsonLd={[
          buildBlogPostingSchema(post),
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
          buildFaqSchema(post.faqs),
        ]}
      />

      <article>
        <header className="bg-brand-accent pt-32 pb-16 text-white md:pt-40 md:pb-20">
          <div className="mx-auto max-w-3xl px-6 md:px-10">
            <Reveal>
              <Link to="/blog" data-testid="blog-back-link" className="mb-6 inline-flex items-center gap-1.5 text-sm text-white/70 transition-colors hover:text-brand-green">
                <ArrowLeft className="h-4 w-4" /> All articles
              </Link>
              <span className="block text-sm font-semibold uppercase tracking-[0.2em] text-brand-green">
                {CLUSTER_LABELS[post.cluster]}
              </span>
              <h1 className="mt-4 font-display text-3xl font-semibold leading-tight md:text-4xl lg:text-5xl">
                {post.title}
              </h1>
              <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-white/70">
                <span className="flex items-center gap-1.5"><User className="h-4 w-4" /> By {CONTACT.name}</span>
                <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {fmtDate(post.date)}</span>
                <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {post.readMins} min read</span>
              </div>
            </Reveal>
          </div>
        </header>

        <div className="bg-white">
          <div className="mx-auto max-w-3xl px-6 py-14 md:px-10 md:py-20">
            <Reveal>
              <div className="mb-10 aspect-[16/9] overflow-hidden rounded-2xl border border-brand-line">
                <img src={post.image} alt={post.title} width="1200" height="675" loading="eager" fetchPriority="high" className="h-full w-full object-cover" />
              </div>
            </Reveal>

            <Reveal>
              <div className="space-y-5">
                {post.intro.map((p, i) => (
                  <RichText key={i} text={p} className="text-lg leading-relaxed text-brand-dark" />
                ))}
              </div>
            </Reveal>

            {post.sections.map((sec, si) => (
              <Reveal key={si}>
                <div className="mt-12">
                  <h2 className="font-display text-2xl font-semibold text-brand-dark md:text-3xl">{sec.h2}</h2>
                  {sec.body && (
                    <div className="mt-4 space-y-4">
                      {sec.body.map((p, i) => (
                        <RichText key={i} text={p} className="leading-relaxed text-brand-muted" />
                      ))}
                    </div>
                  )}
                  {sec.list && (
                    <ul className="mt-5 space-y-2.5">
                      {sec.list.map((it) => (
                        <li key={it} className="flex items-start gap-3 rounded-xl border border-brand-line bg-brand-soft p-3.5">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-blue" />
                          <span className="text-sm text-brand-dark">{it}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Reveal>
            ))}

            {post.closing && (
              <Reveal>
                <div className="mt-12 rounded-2xl border border-brand-blue/20 bg-brand-blue/[0.04] p-7">
                  <h2 className="font-display text-2xl font-semibold text-brand-dark">{post.closing.h2}</h2>
                  <div className="mt-4 space-y-4">
                    {post.closing.body.map((p, i) => (
                      <RichText key={i} text={p} className="leading-relaxed text-brand-muted" />
                    ))}
                  </div>
                </div>
              </Reveal>
            )}

            {post.sources?.length > 0 && <MedicalSources sources={post.sources} />}

            {treatmentLinks.length > 0 && (
              <Reveal>
                <div className="mt-12 rounded-2xl border border-brand-green/30 bg-brand-green/[0.07] p-6" data-testid="related-treatments-strip">
                  <div className="mb-4 flex items-center gap-2">
                    <Stethoscope className="h-5 w-5 text-brand-blue" />
                    <h2 className="font-display text-lg font-semibold text-brand-dark">Related treatments</h2>
                  </div>
                  <div className="flex flex-wrap gap-2.5">
                    {treatmentLinks.map((t) => (
                      <Link
                        key={t.slug}
                        to={`/${t.slug}`}
                        data-testid={`related-treatment-${t.slug}`}
                        className="group inline-flex items-center gap-1.5 rounded-full border border-brand-line bg-white px-4 py-2 text-sm font-medium text-brand-dark transition-all hover:border-brand-blue/50 hover:text-brand-blue hover:shadow-[0_10px_25px_-15px_rgba(2,125,199,0.6)]"
                      >
                        {t.name}
                        <ArrowUpRight className="h-3.5 w-3.5 text-brand-blue transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </Link>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}

            <Reveal>
              <div className="mt-10 flex flex-col gap-5 rounded-2xl border border-brand-line bg-brand-soft p-6 sm:flex-row sm:items-center sm:p-7" data-testid="author-bio-box">
                <img
                  src={IMAGES.doctor}
                  alt={`${CONTACT.name}, ${DOCTOR.role}`}
                  width="112"
                  height="112"
                  loading="lazy"
                  className="h-24 w-24 shrink-0 rounded-full border-2 border-white object-cover shadow-md sm:h-28 sm:w-28"
                />
                <div>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-blue">Written by</span>
                  <h3 className="mt-1 font-display text-xl font-semibold text-brand-dark">
                    <Link to="/dr-ankesh-seth" className="transition-colors hover:text-brand-blue" data-testid="author-bio-name-link">{CONTACT.name}</Link>
                  </h3>
                  <p className="mt-0.5 text-sm text-brand-muted">{DOCTOR.role} · {DOCTOR.yearsExperience} years experience</p>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
                    {DOCTOR.qualifications.slice(0, 3).map((q) => (
                      <span key={q} className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-dark">
                        <BadgeCheck className="h-3.5 w-3.5 text-brand-green" /> {q}
                      </span>
                    ))}
                  </div>
                  <Link
                    to="/dr-ankesh-seth"
                    data-testid="author-bio-more-link"
                    className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-blue"
                  >
                    More about Dr. Seth <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {post.faqs?.length > 0 && <FaqSection faqs={post.faqs} muted />}

        {related.length > 0 && (
          <section className="bg-white">
            <div className="mx-auto max-w-[1440px] px-6 py-20 md:px-12 lg:px-20">
              <h2 className="mb-8 font-display text-3xl font-semibold text-brand-dark">Related reading</h2>
              <div className="grid gap-6 md:grid-cols-3">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    data-testid={`related-post-${r.slug}`}
                    className="group flex h-full flex-col rounded-2xl border border-brand-line bg-white p-6 transition-all hover:-translate-y-1 hover:border-brand-blue/40 hover:shadow-[0_30px_60px_-35px_rgba(2,125,199,0.5)]"
                  >
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-brand-blue">{CLUSTER_LABELS[r.cluster]}</span>
                    <h3 className="mt-2 flex-1 font-display text-base font-medium leading-snug text-brand-dark">{r.title}</h3>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-blue">
                      Read <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>

      <CtaBand title="Ready to book your visit?" text="Get honest advice and painless treatment at NowTreat, Paharia. Call or WhatsApp Dr. Ankesh Seth's team today." />
    </div>
  );
}
